import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Home } from 'lucide-react';
import { HelpArticleCard } from '@/components/help/HelpArticleCard';
import { useHelpCategories, useHelpArticles } from '@/hooks/useHelp';

export function HelpCategory() {
  const { t } = useTranslation();
  const { category: categorySlug } = useParams<{ category: string }>();

  const categoriesQuery = useHelpCategories();
  const articlesQuery = useHelpArticles();

  const categories = categoriesQuery.data ?? [];
  const allArticles = articlesQuery.data ?? [];

  const currentCategory = categories.find((c) => c.slug === categorySlug);
  const categoryArticles = allArticles.filter((a) => a.category_slug === categorySlug);

  if (categoriesQuery.isLoading || articlesQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <div className="text-gray-500">{t('app.loading', 'Indlæser...')}</div>
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-page-light dark:bg-page-dark">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-card-dark">
            <p className="text-gray-500 dark:text-gray-400">
              {t('help.category_not_found', 'Kategori ikke fundet')}
            </p>
            <Link
              to="/help"
              className="mt-4 inline-block text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
            >
              {t('help.back_to_center', 'Tilbage til hjælpecenter')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="mx-auto max-w-7xl px-6 py-12">
        {/* Breadcrumb */}
        <nav className="mb-6 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Link to="/help" className="flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400">
            <Home className="h-4 w-4" />
            {t('help.home', 'Hjælpecenter')}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-medium text-gray-900 dark:text-white">{currentCategory.title}</span>
        </nav>

        {/* Category header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {currentCategory.title}
          </h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            {currentCategory.description}
          </p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
            {categoryArticles.length} {categoryArticles.length === 1 ? 'artikel' : 'artikler'}
          </p>
        </div>

        {/* Articles list */}
        {categoryArticles.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-card-dark">
            <p className="text-gray-500 dark:text-gray-400">
              {t('help.no_articles', 'Ingen artikler i denne kategori endnu')}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {categoryArticles.map((article) => (
              <HelpArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {/* Other categories */}
        <div className="mt-12 border-t border-gray-200 pt-8 dark:border-gray-700">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t('help.other_categories', 'Andre kategorier')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {categories
              .filter((c) => c.slug !== categorySlug)
              .map((category) => (
                <Link
                  key={category.id}
                  to={`/help/${category.slug}`}
                  className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-indigo-600 dark:hover:bg-indigo-900/20"
                >
                  {category.title}
                </Link>
              ))}
          </div>
        </div>
      </main>
    </div>
  );
}
